import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";

import DialogShell from "../components/DialogShell";

export interface ConfirmOptions {
  message: string;
  /** 実行側のボタンの文言。何が起きるかを書く(「OK」にしない) */
  confirmLabel: string;
  /** 取り消せない操作は赤系にする */
  tone?: "default" | "danger";
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

/**
 * 確認を取る。`window.confirm` の置き換え。
 *
 * ホーム画面に追加した PWA だと `window.confirm` はブラウザの見た目で出るため、
 * そこだけアプリの外側に見える。返り値の使い方は同じにしてある。
 */
export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (ask === null) throw new Error("ConfirmProvider の外では使えません");
  return ask;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  const decide = useRef<((ok: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    return new Promise<boolean>((resolve) => {
      // 前の問いが残っていたら、答えないまま捨てられるので取り消し扱いにする
      decide.current?.(false);
      decide.current = resolve;
      setPending(options);
    });
  }, []);

  function answer(ok: boolean) {
    decide.current?.(ok);
    decide.current = null;
    setPending(null);
  }

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      {pending !== null && (
        <DialogShell>
          <p className="text-sm whitespace-pre-line text-[var(--color-ink)]">{pending.message}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              // 取り返しが付かない操作は Enter 一発で実行されないよう、こちらに焦点を置く
              autoFocus={pending.tone === "danger"}
              onClick={() => {
                answer(false);
              }}
              className="rounded-lg px-4 py-2 text-sm text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_6%,transparent)]"
            >
              キャンセル
            </button>
            <button
              type="button"
              autoFocus={pending.tone !== "danger"}
              onClick={() => {
                answer(true);
              }}
              className={`rounded-lg px-4 py-2 text-sm text-white transition hover:opacity-85 ${
                pending.tone === "danger" ? "bg-[var(--color-plum)]" : "bg-[var(--color-accent)]"
              }`}
            >
              {pending.confirmLabel}
            </button>
          </div>
        </DialogShell>
      )}
    </ConfirmContext.Provider>
  );
}
